import { LoadingOverlay, Tooltip } from '@mantine/core';
import clsx, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ForwardedRef, forwardRef, ReactNode } from 'react';

// Utility function to merge Tailwind CSS classes
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface Props {
  type?: 'submit' | 'button';
  disabled?: boolean;
  theme?: 'primary' | 'secondary' | 'additional';
  children?: ReactNode;
  className?: string;
  formId?: string;
  tooltip?: ReactNode;
  onClick?: () => void;
  loading?: boolean;
}

const ButtonPreset = (
  {
    type = 'submit',
    disabled = false,
    theme = 'primary',
    children,
    className = '',
    formId,
    tooltip,
    onClick = () => undefined,
    loading = false,
  }: Props,
  ref: ForwardedRef<HTMLButtonElement>,
) => {
  const getClassName = () => {
    const baseClassName = clsx(
      `w-full relative flex select-none flex-nowrap items-center justify-center gap-2 leading-none outline-none transition-all duration-200`,
    );

    if (disabled) {
      return `${baseClassName} p-2 text-deep-gray-600`;
    }

    switch (theme) {
      case 'primary':
        return cn(
          `${baseClassName} p-2 text-deep-gray-300 bg-deep-gray-300 bg-opacity-10 hover:bg-opacity-20`,
          className,
        );
      case 'additional':
        return cn(
          `${baseClassName} p-2 text-purple-500 hover:bg-purple-500 hover:bg-opacity-10`,
          className,
        );
      case 'secondary':
      default:
        return cn(
          `${baseClassName} p-2 text-deep-gray-300 hover:bg-deep-gray-700`,
          className,
        );
    }
  };

  return (
    <Tooltip label={tooltip} disabled={!tooltip}>
      <div className="relative overflow-hidden rounded-lg">
        <LoadingOverlay
          visible={loading}
          overlayProps={{
            opacity: 0.8,
            color: 'black',
          }}
          loaderProps={{
            size: 'xs',
            color: 'white',
          }}
        />
        <button
          ref={ref}
          disabled={disabled}
          type={type}
          className={getClassName()}
          onClick={onClick}
          form={formId}
        >
          {children}
        </button>
      </div>
    </Tooltip>
  );
};

export const Button = forwardRef(ButtonPreset);